'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { NotesList } from '@nx-frontend/features/notes-list';
import { Head } from '@nx-frontend/shared/ui';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $members } from '@nx-frontend/shared/store/members';
import {
  $notes,
  fetchNotesEvent,
  setNotesSortByEvent,
  setNotesSortOrderEvent,
  NotesSortBy,
  resetFetchEvent,
  SortOrder,
} from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { MenuItemType, NoteDto } from '@nx-frontend/shared/types';
import { NoteCounterType, NoteData, NoteType } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps } from '@nx-frontend/shared/types';
import { getSortFn } from '@nx-frontend/shared/utils';
import { AllDocsButton } from '@nx-frontend/webnotes-client/shared/ui';
import { Topbar } from '@nx-frontend/widgets/topbar';
import { useGetFoldersAllDocsButtons } from './folder-data';
import { WebnotesTopbarFolderItems } from './webnotes-topbar-folder-items';

interface FolderProps {
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
}

export function Folder({ getOpenPaywallPopup }: FolderProps) {
  const lazyLoadingFoldersMark = useRef<HTMLDivElement>(null);
  const lazyLoadingNotesMark = useRef<HTMLDivElement>(null);

  const [currentWorkspace, notes, members, orgInfo] = useUnit([$currentWorkspace, $notes, $members, $orgInfo]);
  const [fetchNotes, setNotesSortBy, setNotesSortOrder, resetFetch] = useUnit([
    fetchNotesEvent,
    setNotesSortByEvent,
    setNotesSortOrderEvent,
    resetFetchEvent,
  ]);

  const params = useParams();
  const slug = useMemo(() => params?.slug || [], [params?.slug]) as string[];
  const [workspaceId, , globalId] = slug || [];

  const sortBy = notes.sortBy;
  const sortOrder = notes.sortOrder;

  const t = useTranslations();

  const getFoldersAllDocsButtons = useGetFoldersAllDocsButtons();
  const foldersAllDocsButtons = useMemo(() => {
    return getFoldersAllDocsButtons();
  }, [getFoldersAllDocsButtons]);

  const handleSortClick = useCallback(
    (type: NotesSortBy) => {
      const order = sortOrder === 'ASC' ? 'DESC' : 'ASC';
      const newOrder = type === sortBy ? order : sortOrder;
      setNotesSortBy(type);
      setNotesSortOrder(newOrder);
      if (sortOrder !== newOrder) {
        resetFetch({ workspaceId, fetchId: 'note-' + globalId });
      }
    },
    [sortOrder, sortBy, workspaceId, globalId, setNotesSortBy, setNotesSortOrder, resetFetch]
  );

  const sortIcon = (
    <Icon
      icon={sortOrder === 'ASC' ? IconsMap.ARROW_UP_1 : IconsMap.ARROW_DOWN_1}
      className="w-3 h-3 text-neutral-500"
    />
  );

  const showAuthor = members && members.count > 1;

  const folderMenuItem: NoteData | undefined = useMemo(() => {
    const folder = notes.workspaces[currentWorkspace.globalId]?.entities[globalId];
    if (folder) {
      return {
        id: `${MenuItemType.NOTES_FOLDER}#${folder.globalId}`,
        type: MenuItemType.NOTES_FOLDER,
        globalId: folder.globalId,
        workspaceId: folder.workspaceId,
        name: folder.title,
        icon: folder.emoji || 'folder',
        userId: folder.userId,
        updatedAt: folder.updatedAt,
        createdAt: folder.createdAt,
        parentId: `${MenuItemType.NOTES_FOLDER}#${folder.parentId}`,
        index: 0,
        visible: true,
        url: '',
        slug: '',
        openInNewTab: false,
        hasChildren: true,
        shared: folder.shared,
      };
    }
  }, [notes, currentWorkspace.globalId, globalId]);

  const loadMoreItems = useCallback(
    (type: NoteType, order: SortOrder) => {
      const countersParams: {
        counters?: boolean;
        countersTypes?: NoteCounterType[];
      } =
        type === 'folder'
          ? {
              counters: true,
              countersTypes: ['children'],
            }
          : {};
      fetchNotes({
        workspaceId: currentWorkspace.globalId,
        type,
        parentId: globalId,
        range: {
          offset: notes.workspaces[currentWorkspace.globalId]?.fetch?.[`${type}-${globalId}`]?.offset || 0,
          limit: 100,
        },
        fetchPortalsNotes: false,
        order: [sortBy, order ?? sortOrder],
        ...countersParams,
      });
    },
    [notes.workspaces, currentWorkspace.globalId, globalId, sortBy, sortOrder, fetchNotes]
  );

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    if (lazyLoadingFoldersMark.current) {
      observer = new IntersectionObserver(
        (entites: IntersectionObserverEntry[]) => {
          const [mark] = entites;
          if (mark.isIntersecting) {
            // once mark is visible on page, load more notes
            loadMoreItems('folder', sortOrder);
          }
        },
        { root: null, rootMargin: '0px', threshold: 1.0 }
      );
      observer.observe(lazyLoadingFoldersMark.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [sortOrder, loadMoreItems]);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    if (lazyLoadingNotesMark.current) {
      observer = new IntersectionObserver(
        (entites: IntersectionObserverEntry[]) => {
          const [mark] = entites;
          if (mark.isIntersecting) {
            // once mark is visible on page, load more notes
            loadMoreItems('note', sortOrder);
          }
        },
        { root: null, rootMargin: '0px', threshold: 1.0 }
      );
      observer.observe(lazyLoadingNotesMark.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, [sortOrder, loadMoreItems]);

  const folders: NoteData[] =
    Object.values(notes.workspaces[currentWorkspace.globalId]?.entities || {})
      .filter(
        (item) => item.type === 'folder' && item.parentId === globalId && !item.inTrash && !item.isSharedForPortal
      )
      .sort(getSortFn<NoteDto>(sortBy, sortOrder))
      .map((note) => ({
        id: `${MenuItemType.NOTES_FOLDER}#${note.globalId}`,
        type: MenuItemType.NOTES_FOLDER,
        globalId: note.globalId,
        workspaceId: note.workspaceId,
        name: note.title,
        icon: note.emoji || 'folder',
        userId: note.userId,
        updatedAt: note.updatedAt,
        createdAt: note.createdAt,
        parentId: `${MenuItemType.NOTES_FOLDER}#${note.parentId}`,
        index: 0,
        visible: true,
        url: '',
        slug: '',
        openInNewTab: false,
        hasChildren: true,
        shared: note.shared,
        childrenFoldersCount: note.childrenFoldersCount,
        childrenNotesCount: note.childrenNotesCount,
      })) || [];

  const folderNotes: NoteData[] =
    Object.values(notes.workspaces[currentWorkspace.globalId]?.entities || {})
      .filter((item) => item.type === 'note' && item.parentId === globalId && !item.inTrash && !item.isSharedForPortal)
      .sort(getSortFn<NoteDto>(sortBy, sortOrder))
      .map((note) => ({
        id: `${MenuItemType.NOTE}#${note.globalId}`,
        type: MenuItemType.NOTE,
        globalId: note.globalId,
        workspaceId: note.workspaceId,
        name: note.title,
        icon: note.emoji || 'page',
        userId: note.userId,
        updatedAt: note.updatedAt,
        createdAt: note.createdAt,
        parentId: `${MenuItemType.NOTES_FOLDER}#${note.parentId}`,
        index: 0,
        visible: true,
        url: '',
        slug: '',
        openInNewTab: false,
        hasChildren: false,
        shared: note.shared,
      })) || [];

  const notesFetch = notes?.workspaces[currentWorkspace.globalId]?.fetch || {};
  const folderName = globalId === 'root' ? t('webnotes.webnotes-folders') : folderMenuItem?.name;
  const pageTitle = `${folderName ?? t('webnotes.webnotes-folders')} - ${currentWorkspace.title}`;

  /**
   * Topbar title for folders page
   * If folder globalId is root, show folders title
   * Otherwise, show instead title will show breadcrumbs
   */
  const topbarTitle = useMemo(() => {
    if (globalId === 'root') {
      return (
        <div className="flex items-end gap-2">
          <Icon icon={IconsMap.FOLDER} className="w-6 h-6 text-neutral-500" />
          <span className="text-4 ">{t('webnotes.webnotes-folders')}</span>
        </div>
      );
    }
    return null;
  }, [t, globalId]);

  return (
    <div className="flex flex-col w-full h-full">
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <Topbar breadcrumbsArea={topbarTitle} cy="folders-header" bradcrumbsWrapperClassName="max-w-[540px] mr-auto">
        <WebnotesTopbarFolderItems />
      </Topbar>
      <div
        data-cy="folders-content"
        className="flex flex-col items-start gap-6 w-full h-full pt-7 py-6 overflow-y-scroll"
        id="main-scrolling-container"
      >
        <div className="flex w-full pl-[41px] pr-[64px] gap-[16px]">
          {foldersAllDocsButtons
            ? foldersAllDocsButtons.map((button, index) => (
                <AllDocsButton
                  orgId={currentWorkspace.orgId}
                  subscriptionType={orgInfo.subscriptionType}
                  key={index}
                  buttonData={button}
                  folderId={globalId}
                  getOpenPaywallPopup={getOpenPaywallPopup}
                />
              ))
            : null}
        </div>
        <div className="pl-[41px] pr-[64px] flex flex-col w-full items-start gap-4">
          {/* <div className="flex items-start gap-[20px] pl-4">
            <span className="text-[10px] py-1">ALL</span>
            <span className="text-[10px] py-1">SHARED</span>
          </div> */}
          <div className="w-full h-full">
            <table data-cy="folders-table" className="table w-full">
              <thead data-cy="folders-table-header" className="table-header-group">
                <tr className="table-row border-b-[1px] border-solid border-neutral-200">
                  <th
                    style={{ width: '51.6%' }}
                    className="table-cell text-left hover:bg-zinc-50 cursor-pointer"
                    onClick={() => handleSortClick('title')}
                  >
                    <div className="py-2.5 px-4 flex items-center gap-1">
                      <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-name')}</span>
                      {sortBy === 'title' ? sortIcon : null}
                    </div>
                  </th>
                  {showAuthor ? (
                    <th className="table-cell text-left" style={{ width: '21.7%' }}>
                      <div className="flex items-center p-2">
                        <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-author')}</span>
                      </div>
                    </th>
                  ) : null}
                  <th
                    className="table-cell text-left hover:bg-zinc-50 cursor-pointer"
                    style={{ width: '11.1%' }}
                    onClick={() => handleSortClick('updatedAt')}
                  >
                    <div className="p-2 flex items-center gap-1">
                      <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-updated')}</span>
                      {sortBy === 'updatedAt' ? sortIcon : null}
                    </div>
                  </th>
                  <th
                    style={{ width: '11.1%' }}
                    className="table-cell text-left hover:bg-zinc-50 cursor-pointer"
                    onClick={() => handleSortClick('createdAt')}
                  >
                    <div className="p-2 flex items-center gap-1">
                      <span className="text-[10px] font-normal uppercase">{t('webnotes.webnotes-created')}</span>
                      {sortBy === 'createdAt' ? sortIcon : null}
                    </div>
                  </th>
                  <th style={{ width: '4.5%' }}></th>
                </tr>
              </thead>

              <tbody className="table-row-group">
                <tr>
                  <td>
                    <div className="table-caption px-4 py-1 mt-4 text-[10px] font-normal text-neutral-500 uppercase">
                      {t('webnotes.webnotes-folders')}
                    </div>
                  </td>
                </tr>

                <NotesList items={folders} showAuthor={showAuthor} />
                <tr>
                  <td colSpan={4}>
                    <div ref={lazyLoadingFoldersMark}></div>
                  </td>
                </tr>
                {notesFetch?.['folder-' + globalId]?.isFetching && (
                  <tr>
                    <td colSpan={4}>
                      <div className="flex flex-col items-center justify-center h-[52px] my-1">
                        <Spinner />
                      </div>
                    </td>
                  </tr>
                )}

                {globalId !== 'root' ? (
                  <>
                    <tr>
                      <td>
                        <div className="table-caption px-4 py-1 mt-4 text-[10px] font-normal text-neutral-500 uppercase">
                          {t('webnotes.webnotes-pages')}
                        </div>
                      </td>
                    </tr>

                    <NotesList items={folderNotes} showAuthor={showAuthor} />
                    <tr>
                      <td colSpan={4}>
                        <div ref={lazyLoadingNotesMark}></div>
                      </td>
                    </tr>
                    {notesFetch?.['note-' + globalId]?.isFetching && (
                      <tr>
                        <td colSpan={4}>
                          <div className="flex flex-col items-center justify-center h-[52px] my-1">
                            <Spinner />
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
